import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { optOutDripContact } from '../../../../../common/api/dripContacts';
import { useEffect } from 'react';
import { ViewLayout } from '../../../../../components/ViewLayout';
import {
  Alert,
  Box,
  Button,
  Container,
  FormField,
  Grid,
  Header,
  SpaceBetween,
} from '@cloudscape-design/components';
import { LoadingOrError } from '../../../../../components/Loading';

export default function DripContactOptOutPage() {
  const { uuid } = useParams({ from: '/drip/contacts/$uuid/opt-out' });

  const mutation = useMutation({
    mutationFn: async (req: { opt_out: boolean }): Promise<{ opt_out: boolean }> => {
      if (!uuid) return { opt_out: true };
      await optOutDripContact(uuid, req);
      return req;
    },
    retry: 3, // safe to retry up to 3 times
    // exponential backoff
    retryDelay: (attempt) => Math.min(attempt > 1 ? 2 ** attempt * 1000 : 1000, 30 * 1000), // 1s, 2s, 4s, 8s, 16s, 30s
  });

  useEffect(() => {
    if (!uuid) return;
    if (mutation.isPending) return;
    mutation.mutate({ opt_out: true });
  }, [uuid]);

  const handleOptIn = () => {
    if (!uuid) return;
    if (mutation.isPending) return;
    mutation.mutate({ opt_out: false });
  };

  const handleOptOut = () => {
    if (!uuid) return;
    if (mutation.isPending) return;
    mutation.mutate({ opt_out: true });
  };

  return (
    <ViewLayout
      breadcrumbs={[
        { text: 'Emails', href: '' },
        { text: 'Opt Out', href: '' },
      ]}
      navigationHide
      header={
        <Header description="Opt out of Review Emails from Real Time Feedback" variant="h1">
          Opt Out of Review Emails
        </Header>
      }
    >
      <Grid
        gridDefinition={[
          {
            colspan: {
              default: 12,
              l: 8,
              s: 6,
            },
          },
        ]}
      >
        <Container header={<Header variant="h2">Email Consent</Header>}>
          <SpaceBetween size="l">
            {/* Introductory Text */}
            <Box variant="p">
              To comply with our email policies, please provide your consent below if you would like
              to receive email updates regarding Google reviews.
            </Box>

            {/* Consent Form */}
            <SpaceBetween direction="vertical" size="l">
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  variant="primary"
                  onClick={handleOptIn}
                  disabled={mutation.isPending || (!mutation.isPending && !mutation.data?.opt_out)}
                >
                  Opt In
                </Button>
                <Button
                  variant="normal"
                  onClick={handleOptOut}
                  disabled={mutation.isPending || (!mutation.isPending && mutation.data?.opt_out)}
                >
                  Opt Out
                </Button>
              </SpaceBetween>
            </SpaceBetween>
            {(mutation.isPending || mutation.error) && (
              <LoadingOrError loading={mutation.isPending} error={mutation.error} />
            )}

            {/* Display Confirmation Message */}
            {!mutation.isPending && (
              <Alert type="success">
                {mutation.data?.opt_out
                  ? 'You have opted out of receiving emails.'
                  : 'You have opted in to receiving emails.'}
              </Alert>
            )}
          </SpaceBetween>
        </Container>
      </Grid>
    </ViewLayout>
  );
}
